import { useEffect, useState } from 'react'
import {
    ScoopRow,
    ScoopText,
    ScoopChip,
    ScoopButton,
    ScoopSpinner,
    ScoopColumn,
    ScoopStatusBubble,
    ScoopCircularProgress,
} from '../../../ui'
import {
    DotsThreeIcon,
    TableIcon,
    EnvelopeIcon,
    CheckerboardIcon,
    FileArrowUpIcon,
    DatabaseIcon,
} from '@phosphor-icons/react'
import styles from './DatasetRow.module.scss'
import { useNavigate } from 'react-router-dom'
import { formatDate } from '../../../../utils/date'
import { sources } from '../NewDatasetModal/ConnectDatasource/ConnectDatasource'

const identifyTrue = (transactional) => {
    if (transactional) return 'Transactional'
    return 'Snapshot' // Return this if none are true
}

export const DatasetRow = ({
    dataset,
    setAnchorEl,
    setSelectedInbox,
    id,
    processStatus,
    isRunning,
}) => {
    const navigate = useNavigate()
    const { label, description, tables, transactional } = dataset

    const [loading, setLoading] = useState(false)
    const [progress, setProgress] = useState(null)

    useEffect(() => {
        const status = processStatus.get(dataset.inboxID)
        if (status) {
            setLoading(true)
            if (status.curStep === -1 || (status.totalSteps === 1 && status.numRecords > 0)) {
                if (status.numRecords > 0) {
                    if (status.totalRecords > 0) {
                        let val = (status.numRecords * 100) / status.totalRecords
                        setProgress(status.curStep === -1 ? 'Starting' : `${val.toFixed(1)}%`)
                    } else {
                        setProgress((status.numRecords ?? 0).toLocaleString())
                    }
                } else {
                    setProgress('Starting')
                }
            } else if (status.curStep >= 0) {
                let val = status.totalSteps > 0 ? (status.curStep * 100) / status.totalSteps : 0
                setProgress(status.curStep === -1 ? 'Starting' : `${val.toFixed(1)}%`)
            }
        } else {
            setLoading(false)
        }
    }, [processStatus, dataset.inboxID])

    const getIcon = () => {
        const iconProps = { size: 22, weight: 'regular' }

        switch (dataset.inboxType) {
            case 'email':
                return <EnvelopeIcon {...iconProps} />
            case 'robot':
                if (dataset.botService) return <DatabaseIcon {...iconProps} />
                return <FileArrowUpIcon {...iconProps} />
            case 'calculated':
                return <CheckerboardIcon {...iconProps} />
            case 'api':
                // For API datasets, still use the icon from sources array (PNG/SVG)
                const apiSourceIcon = sources.find(
                    (source) => source?.sourceValue === dataset?.extractDefinition?.connectorType
                )?.icon
                return apiSourceIcon ? (
                    <img
                        src={apiSourceIcon}
                        alt="api-source-icon"
                        className={styles.apiSourceIcon}
                    />
                ) : (
                    <DatabaseIcon {...iconProps} />
                )
            default:
                return <DatabaseIcon {...iconProps} />
        }
    }

    const lastIngestion =
        dataset.tables.length > 0
            ? dataset.tables.reduce((latest, current) => {
                  return new Date(current.lastIngestion) > new Date(latest.lastIngestion)
                      ? current
                      : latest
              }).lastIngestion
            : null

    return (
        <div
            key={id}
            className={`${styles.datasetRow} ${loading ? styles.datasetRowDisabled : ''}`}
            onClick={() => navigate(`${dataset.inboxID}`)}
        >
            <div
                className={`${styles.iconWrapper} ${dataset.inboxType !== 'api' ? styles.datasetTypeIcon : ''}`}
            >
                {getIcon()}
            </div>
            <ScoopRow className={styles.datasetRowContent}>
                <ScoopColumn className={styles.listItemText}>
                    <ScoopText size="md" className={styles.primaryText}>
                        {label}
                    </ScoopText>
                    <ScoopText size="sm" className={styles.secondaryText}>
                        {description}
                    </ScoopText>
                </ScoopColumn>
                <ScoopRow className={styles.chipContainer}>
                    <ScoopChip label={identifyTrue(transactional)} size="small" />
                </ScoopRow>
                <ScoopChip size="small">
                    <TableIcon size={16} className={styles.datasetTypeIcon} />
                    {tables.length}
                </ScoopChip>
            </ScoopRow>
            <ScoopRow className={styles.datasetRowRight}>
                {loading ? (
                    <>
                        <ScoopRow className={styles.datasetRowProgress}>
                            <ScoopText className={styles.lastScoopDate}>
                                Progress {progress}
                            </ScoopText>
                            <ScoopCircularProgress
                                className={`${styles.progressSpinner} ${styles.progressSize}`}
                                variant={
                                    progress?.endsWith?.('%') ? 'determinate' : 'indeterminate'
                                }
                                value={parseFloat(progress) || 0}
                                size={20}
                            />
                        </ScoopRow>
                    </>
                ) : (
                    <>
                        <ScoopText className={styles.lastScoopDate}>
                            {formatDate(lastIngestion)
                                ? `Last Scoop ${formatDate(lastIngestion)}`
                                : ''}
                        </ScoopText>
                        <ScoopStatusBubble status={'success'} />
                    </>
                )}
            </ScoopRow>
            {!dataset.readOnly && isRunning ? (
                <ScoopSpinner />
            ) : (
                <ScoopButton
                    variant="text"
                    size="xxs"
                    className={styles.moreButton}
                    onClick={(event) => {
                        event.stopPropagation()
                        setAnchorEl(event.currentTarget)
                        setSelectedInbox(dataset)
                    }}
                >
                    <DotsThreeIcon size={18} />
                </ScoopButton>
            )}
        </div>
    )
}
